import { DownLoadPhotosTypesEnum } from '@lite-workspace/dto-fotoclass-photo';
import { TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';
import { PrintTechnology } from '@lite-workspace/photobook-model';
import { ClientSelectionSettings } from './client-selection.dto';
import { QuotesSettings } from './quotes.dto';
import { StudentDownloadSettings } from './student-download.dto';
import { UpsaleSettings } from './upsale-settings.dto';
import type { UpsaleSalesTotals } from './upsale.dto';
import {
  FC_VignettePortraitCropRule_DTO,
  GroupSpreadSettings,
  IndividualSpreadSettings,
  PersonalSpreadSettings,
  SpreadSlotTypeEnum,
  VignetteType,
} from './spread.dto';

export type ProjectTextSettingKey =
  | TextTypeEnum.SCHOOL
  | TextTypeEnum.GRADUATION_YEAR
  | TextTypeEnum.CLASS
  | TextTypeEnum.CITY
  | VignetteTextTypeEnum;

/**
 * Формат рендера PERSON_FULL_NAME на уровне альбома.
 * `SURNAME_FIRST_PATRONYMIC` — «Иванов Иван Иванович» (default).
 * `FIRST_PATRONYMIC_SURNAME` — «Иван Иванович Иванов».
 * `SURNAME_INITIALS` — «Иванов И.И.» (для отсутствующего отчества → «Иванов И.»).
 * `SURNAME_BREAK_FIRST_PATRONYMIC` — «Иванов / Иван Иванович» (« / » = перенос строки).
 * `FIRST_PATRONYMIC_BREAK_SURNAME` — «Иван Иванович / Иванов».
 * `FIRST_ONLY` — «Иван».
 * `INITIALS_SURNAME` — «И. И. Иванов».
 * `SURNAME_BREAK_FIRST_BREAK_PATRONYMIC` — «Иванов / Иван / Иванович» (педагоги, три строки).
 * `FIRST_BREAK_PATRONYMIC_BREAK_SURNAME` — «Иван / Иванович / Иванов» (педагоги, три строки).
 */
export enum NameFormat {
  SURNAME_FIRST_PATRONYMIC = 'sur-first-pat',
  FIRST_PATRONYMIC_SURNAME = 'first-pat-sur',
  SURNAME_INITIALS = 'sur-init',
  SURNAME_BREAK_FIRST_PATRONYMIC = 'sur-br-first-pat',
  FIRST_PATRONYMIC_BREAK_SURNAME = 'first-pat-br-sur',
  FIRST_ONLY = 'first',
  INITIALS_SURNAME = 'init-sur',
  SURNAME_BREAK_FIRST_BREAK_PATRONYMIC = 'sur-br-first-br-pat',
  FIRST_BREAK_PATRONYMIC_BREAK_SURNAME = 'first-br-pat-br-sur',
}

/** Подписи учеников на виньетке — 6 форматов «Подписи → Ученики». */
export const STUDENT_NAME_FORMATS = [
  NameFormat.SURNAME_FIRST_PATRONYMIC,
  NameFormat.FIRST_PATRONYMIC_SURNAME,
  NameFormat.SURNAME_BREAK_FIRST_PATRONYMIC,
  NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME,
  NameFormat.SURNAME_INITIALS,
  NameFormat.FIRST_ONLY,
] as const;
export type StudentNameFormat = (typeof STUDENT_NAME_FORMATS)[number];

/** Подписи педагогов на виньетке — 7 форматов «Подписи → Педагоги»: в одну, две и три строки. */
export const TEACHER_NAME_FORMATS = [
  NameFormat.SURNAME_FIRST_PATRONYMIC,
  NameFormat.SURNAME_BREAK_FIRST_PATRONYMIC,
  NameFormat.FIRST_PATRONYMIC_BREAK_SURNAME,
  NameFormat.SURNAME_INITIALS,
  NameFormat.INITIALS_SURNAME,
  NameFormat.SURNAME_BREAK_FIRST_BREAK_PATRONYMIC,
  NameFormat.FIRST_BREAK_PATRONYMIC_BREAK_SURNAME,
] as const;
export type TeacherNameFormat = (typeof TEACHER_NAME_FORMATS)[number];

export const VIGNETTE_DESIGNS = ['lumir', 'pack'] as const;
export const VIGNETTE_TEACHERS_PLACES = ['grid', 'left-page', 'own-spread', 'none'] as const;
export const VIGNETTE_QUOTES = ['lead', 'students', 'none'] as const;
export const VIGNETTE_CROPS = ['chest', 'shoulders', 'close'] as const;
export const VIGNETTE_TAILS = ['level', 'fill', 'even'] as const;
export const VIGNETTE_EMPTY_FILLS = ['class-name', 'class-quote', 'pack-decor', 'group-photo'] as const;
export const VIGNETTE_QUOTE_GROUPS = ['lead', 'teachers', 'students', 'owner'] as const;
export type VignetteQuoteGroup = (typeof VIGNETTE_QUOTE_GROUPS)[number];
/** Переключатель цитат группы: `null` = авто (включён, когда у группы есть хоть одна цитата). */
export type VignetteQuoteGroups = Record<VignetteQuoteGroup, boolean | null>;
/** Вариант на 3 карточки снят (T1.11): сохранённое 3 читается как 4 (`pickVignetteKnobs`), новая запись 3 — 422. */
export const VIGNETTE_CARDS_PER_PAGE = [1, 2, 4] as const;
/** `alternate` = ряды чередуют сторону текста: 1-й ряд справа, 2-й слева…, заново на каждой странице. */
export const VIGNETTE_CARD_TEXT_PLACEMENTS = ['right', 'left', 'below', 'alternate'] as const;
export const VIGNETTE_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export const VIGNETTE_QUOTE_VERTICAL_ALIGNS = ['top', 'center', 'bottom'] as const;
export type VignetteQuoteVerticalAlign = (typeof VIGNETTE_QUOTE_VERTICAL_ALIGNS)[number];
export const VIGNETTE_TEXT_WEIGHTS = ['regular', 'bold'] as const;
/** Кегль текста карточки, пт: границы ручки «Размер». */
export const VIGNETTE_TEXT_SIZE_MIN = 6;
export const VIGNETTE_TEXT_SIZE_MAX = 40;

/** Начертание текста карточки (ФИО, цитата). */
export interface VignetteTextStyle {
  /** Кегль, пт; `null` = кегль раскладки по умолчанию. */
  size: number | null;
  weight: (typeof VIGNETTE_TEXT_WEIGHTS)[number];
  italic: boolean;
  caps: boolean;
  /** `#rrggbb`; `null` = цвет текста по умолчанию. */
  color: string | null;
}

export const VIGNETTE_TEXT_STYLE_DEFAULTS: Readonly<VignetteTextStyle> = {
  size: null,
  weight: 'regular',
  italic: false,
  caps: false,
  color: null,
};

/** Кегли подписей класса на виньетке, в единицах кегля рамки; не задан — кегль шаблона. */
export interface VignetteCaptionSizes {
  student?: number;
  teacher?: number;
  subject?: number;
}

export const VIGNETTE_CAPTION_SIZE_KEYS = ['student', 'teacher', 'subject'] as const;
/**
 * Потолок кегля класса, px книги: выше любого кегля, что дают поля размера редактора (800 px и 200 pt = 833 px при
 * 300 dpi печати), — сервер не отклоняет кегль, который фотограф набрал.
 */
export const VIGNETTE_CAPTION_SIZE_MAX = 1000;

/** Ручки панели «Виньетка»: живут на проекте (`photoBook.vignette`), пишет только фотограф. */
export interface VignetteKnobs {
  design: (typeof VIGNETTE_DESIGNS)[number];
  teachersPlace: (typeof VIGNETTE_TEACHERS_PLACES)[number];
  leadLarge: boolean;
  teachersLarger: boolean;
  /** Размер портрета ученика в печати, см; `null` = самый мелкий вариант. */
  portraitSize: number | null;
  quotes: (typeof VIGNETTE_QUOTES)[number];
  nameFormat: StudentNameFormat;
  /** `null` = формат пака / движка по умолчанию. */
  teacherNameFormat: TeacherNameFormat | null;
  teacherSubject: boolean;
  /** Один кегль на все ФИО учеников, на все ФИО педагогов и на все предметы; `null` — кегли шаблона. */
  captionSizes: VignetteCaptionSizes | null;
  /**
   * «Подписи → Как в паке»: каждое ФИО виньетки печатается раскладкой своей рамки из пака — у учеников и
   * педагогов, форматы выше не действуют. Только у виньетки пака; у Люмир ручка не читается.
   */
  captionsFromPack: boolean;
  /**
   * Пресет кадра; объект `portraitCropRule` выводится из него (`VIGNETTE_CROP_RULES`) и едет тем же PATCH.
   * `null` = пресета нет: при правиле — свой кадр фотографа, без правила — `VIGNETTE_KNOB_DEFAULTS.crop`.
   */
  crop: (typeof VIGNETTE_CROPS)[number] | null;
  tail: (typeof VIGNETTE_TAILS)[number];
  emptyFill: (typeof VIGNETTE_EMPTY_FILLS)[number];
  classQuote: string;
  /** Цитаты по группам; нет ключа группы = `null` (авто). */
  quoteGroups: VignetteQuoteGroups;
  /** Карточек с цитатой на страницу. */
  cardsPerPage: (typeof VIGNETTE_CARDS_PER_PAGE)[number];
  /** Где текст карточки с цитатой относительно портрета. */
  cardTextPlacement: (typeof VIGNETTE_CARD_TEXT_PLACEMENTS)[number];
  /** Выключка подписи (ФИО) карточки. */
  captionAlign: (typeof VIGNETTE_TEXT_ALIGNS)[number];
  /** Выключка цитаты карточки. */
  quoteAlign: (typeof VIGNETTE_TEXT_ALIGNS)[number];
  /** Цитата по вертикали в своей рамке карточки. */
  quoteVerticalAlign: VignetteQuoteVerticalAlign;
  /** Разделитель между подписью (ФИО) и цитатой карточки. */
  quoteDivider: boolean;
  /** Цвет разделителя, `#rrggbb`; `null` = цвет подписи (ФИО). */
  quoteDividerColor: string | null;
  /** Начертание подписи (ФИО) карточки. */
  captionStyle: VignetteTextStyle;
  /** Кегль предмета педагога, пт; `null` = кегль раскладки по умолчанию. */
  subjectSize: number | null;
  /** Начертание цитаты карточки. */
  quoteStyle: VignetteTextStyle;
}

export const VIGNETTE_KNOB_DEFAULTS: VignetteKnobs = {
  design: 'lumir',
  teachersPlace: 'grid',
  leadLarge: true,
  teachersLarger: true,
  portraitSize: null,
  quotes: 'lead',
  nameFormat: NameFormat.SURNAME_FIRST_PATRONYMIC,
  teacherNameFormat: null,
  teacherSubject: true,
  captionsFromPack: false,
  captionSizes: null,
  crop: 'chest',
  tail: 'fill',
  emptyFill: 'class-name',
  classQuote: '',
  quoteGroups: { lead: null, teachers: null, students: null, owner: null },
  cardsPerPage: 4,
  cardTextPlacement: 'below',
  captionAlign: 'left',
  quoteAlign: 'left',
  // Зазор «имя → цитата» = одна строка имени: по умолчанию цитата прижата к верху.
  quoteVerticalAlign: 'top',
  quoteDivider: false,
  quoteDividerColor: null,
  captionStyle: { ...VIGNETTE_TEXT_STYLE_DEFAULTS },
  subjectSize: null,
  // Кегль цитаты `null` = «Авто»: планировщик подбирает его под портрет (T2.12).
  quoteStyle: { ...VIGNETTE_TEXT_STYLE_DEFAULTS, italic: true },
};

/** Пресет кадра → правило кропа: лицо — доля высоты рамки («По плечи» ≈ 1.25 × «По грудь»), центр как у автокропа. */
export const VIGNETTE_CROP_RULES: Readonly<Record<(typeof VIGNETTE_CROPS)[number], FC_VignettePortraitCropRule_DTO>> = {
  chest: { faceHeightFrac: 0.25, faceCenterXFrac: 0.5, faceCenterYFrac: 0.4 },
  shoulders: { faceHeightFrac: 0.3125, faceCenterXFrac: 0.5, faceCenterYFrac: 0.4 },
  close: { faceHeightFrac: 0.4, faceCenterXFrac: 0.5, faceCenterYFrac: 0.4 },
};

export const VIGNETTE_KNOB_KEYS = Object.keys(VIGNETTE_KNOB_DEFAULTS) as readonly (keyof VignetteKnobs)[];

/** Ручки-объекты: `pickVignetteKnobs` отдаёт их копией. */
const OBJECT_KNOB_KEYS: ReadonlySet<keyof VignetteKnobs> = new Set(['quoteGroups', 'captionStyle', 'quoteStyle', 'captionSizes']);

/** Одно отображение в обе стороны (проект → редактор, редактор → PATCH): только ручки, только заданные. */
export function pickVignetteKnobs(source: object | null | undefined): Partial<VignetteKnobs> {
  const record = (source ?? {}) as Partial<Record<keyof VignetteKnobs, unknown>>;
  const knobs: Partial<Record<keyof VignetteKnobs, unknown>> = {};
  for (const key of VIGNETTE_KNOB_KEYS) {
    const value = record[key];
    if (value === undefined) continue;

    if (key === 'cardsPerPage' && value === 3) {
      knobs[key] = 4;
      continue;
    }

    // Объект копируется: правка прочитанных ручек не должна задеть источник.
    knobs[key] = OBJECT_KNOB_KEYS.has(key) && value !== null && typeof value === 'object' ? { ...value } : value;
  }

  return knobs as Partial<VignetteKnobs>;
}

/** Ключи подписей виньетки в `photoBook.vignette`: формат учеников (и его легаси-флаг), формат педагогов, предмет, кегли. */
export const VIGNETTE_CAPTION_KEYS = [
  'nameFormat',
  'reverseNameOrder',
  'teacherNameFormat',
  'teacherSubject',
  'captionSizes',
] as const;
export type VignetteCaptionKey = (typeof VIGNETTE_CAPTION_KEYS)[number];

/** Только ключи подписей блока виньетки, только заданные. */
export function pickVignetteCaptionKnobs<T extends object>(source: T | null | undefined): Partial<T> {
  const record = (source ?? {}) as Partial<Record<VignetteCaptionKey, unknown>>;
  return Object.fromEntries(
    VIGNETTE_CAPTION_KEYS.flatMap(key => (record[key] === undefined ? [] : [[key, record[key]]])),
  ) as Partial<T>;
}

/** Блок виньетки без ключей подписей: подписи альбома едут к нему только с «Применить». */
export function omitVignetteCaptionKnobs<T extends object>(source: T): Omit<T, VignetteCaptionKey> {
  const rest = { ...source } as Record<string, unknown>;
  for (const key of VIGNETTE_CAPTION_KEYS) delete rest[key];
  return rest as Omit<T, VignetteCaptionKey>;
}

/**
 * Подписи эталона-шаблона проекта (строка без `personId`) — ручки проекта: их показывает и пишет его редактор, а копия
 * в настройках эталона могла отстать (восстановление версии, правка до зеркала). Без ручек на проекте остаётся копия.
 * Эталон, назначенный из альбома ученика, и альбомы учеников редактор ведёт как альбом: подписи он пишет в свою копию,
 * она и едет — в копию эталона, в превью и в печать.
 */
export function withProjectCaptions(
  settings: FC_AlbumSettings_DTO | undefined,
  baseSettings: FC_BaseProjectSettings_DTO | undefined,
  personId: string | null | undefined,
): FC_AlbumSettings_DTO | undefined {
  const captions = pickVignetteCaptionKnobs(baseSettings?.photoBook?.vignette);
  if (personId || !settings?.photoBook || Object.keys(captions).length === 0) return settings;
  return { ...settings, photoBook: { ...settings.photoBook, vignette: { ...settings.photoBook.vignette, ...captions } } };
}

/**
 * Чем упорядочены карточки персон на виньетке. `manual` — раскладка, собранная перетаскиванием:
 * сортировать нечего, порядок уже лежит в модели. Остальные значения — ключ, по которому карточки
 * расставляются заново.
 */
export type VignetteCardOrder = 'surname' | 'file-name' | 'shot-date' | 'manual';

/** Ключ, которым карточки сажают заново; «Ручной» ключом не бывает — он только хранит рассадку. */
export type VignetteSeatingKey = Exclude<VignetteCardOrder, 'manual'>;

/** Порядок карточек у виньеток, созданных до FKS-11920: ключа в настройках нет — читается как алфавит. */
export const DEFAULT_VIGNETTE_CARD_ORDER: VignetteSeatingKey = 'surname';

type UUID = string;

/** Days an archived (trashed) project waits before the purge cron soft-deletes it. */
export const PROJECT_TRASH_PURGE_DAYS = 30;

export interface FC_ProjectDTO {
  id: UUID;
  name: string;
  createdAt: string;
  archived: boolean;
  /** ISO moment the project went to trash; `null` while active. */
  archivedAt: string | null;
  /** ISO moment the project was put away to «Архив» (still fully working); `null` while active. Survives trash and restore. */
  putAwayAt: string | null;
  link: string;
  linkCode: string;
  coordinatorCode?: string;

  openCount?: number;
  lastOpenedAt?: string;
  albumsGenerationCount?: number;

  /** Card cover — the project's first horizontal thumb; served only with `withCounter`. */
  coverUrl?: string;

  /** Master album's pack name (draft: the wizard pick); `null` — free design or the pack was deleted. */
  templateName?: string | null;

  /** Present only while a fresh demo copy may still be rendering; `false` — it is not walkable yet. */
  demoReady?: boolean;

  /** Демо-доступ: эталон (живой или отставленный) либо демо-копия. Отсутствует = реестр недоступен. */
  demoLocked?: boolean;

  /** Selection started: upsale pricing model and prices are read-only (writes answer UPSALE_PRICES_LOCKED). */
  upsalePricesLocked?: boolean;

  /** A counter upstream failed on this page: zeros and «no template» are not facts. */
  countersDegraded?: boolean;

  counter: {
    students: number;
    teachers: number;
    photos: number;
    photobooks: number;
    /** Albums generated for STUDENT persons; absent until the backend ships the field. */
    studentPhotobooks?: number;
    /** Собранные мастер-альбомы (строка без владельца с моделью); absent until the backend ships the field. */
    masterPhotobooks?: number;
    /** `upsaleSalesTotals` over the project bills («Покупки» tiles); absent when off without bills or the read failed. */
    upsale?: UpsaleSalesTotals;
  };

  settings: FC_ProjectSettingsDTO;

  statistics?: FC_ProjectStatisticDTO; // NOTE: dynamic load ProjectStatistic
}

/** Единственный белый список интентов — им же валидируются значения из localStorage и с сервера. */
export const PHOTO_INTENTS = ['all-in-one', 'students-separate', 'students-teachers-separate', 'selected-folders'] as const;

export function isPhotoIntent(value: unknown): value is (typeof PHOTO_INTENTS)[number] {
  return typeof value === 'string' && (PHOTO_INTENTS as readonly string[]).includes(value);
}

/**
 * Выбор шаблона в визарде, сохранённый НА ПРОЕКТЕ: `?design=` и sessionStorage переживают только
 * вкладку, а возврат в новой сессии должен собирать альбом по тому же шаблону.
 */
export interface WizardSelection {
  mode: 'select' | 'upload';
  /** Пакет из каталога; null — встроенный «Без шаблона». */
  selectedDesignId: string | null;
  /** Черновик PSD режима UPLOAD. */
  uploadDesignId?: string;
  /** «Что у вас на руках» — сколько фото-шагов несёт план; отсутствует у проекта, начатого до
   *  селектора (такой проект с фотографиями держит легаси-план из трёх шагов). */
  photoIntent?: (typeof PHOTO_INTENTS)[number];
  /** Подвижный курсор фазы проекта на замороженной 0-based шкале (students 0 · teachers 1 ·
   *  all-photos 2 · persons 3 · albums 4) — в отличие от lastWizardStep ходит в обе стороны. */
  projectPhase?: number;
}

export interface ProjectData {
  lastWizardStep: number | null;
  wizardSelection?: WizardSelection;
  isWatchedMasterPhotoBookVideo?: boolean;
  isSawUnmergedPersonsAlert?: boolean;
  /** Время закрытия баннера о нехватке фотографий (ISO date). Баннер показывается для книг созданных после этого времени */
  photoShortageBannerDismissedAt?: string;
  isDemoProject?: boolean;
  /** Наполнение демо-копии отказало окончательно: сага исчерпала повторы, а строки проекта
   *  остались на месте. Пишет core-service системным патчем; фронт по нему предлагает пересборку. */
  demoAttachFailed?: boolean;
  /** Показывать водяной знак на фотографиях (по умолчанию: true) */
  withWatermark?: boolean;
  /** Время последнего успешного экспорта макета/архива (ISO date), пишется фронтом */
  lastExportedAt?: string;
  /** Скачивание фотографий учеником; отсутствие = дефолты (resolveStudentDownload). */
  studentDownload?: Partial<StudentDownloadSettings>;
  /** Допродажи; отсутствие = выключены, цен нет (resolveUpsaleSettings). */
  upsale?: UpsaleSettings;
  baseSettings?: FC_BaseProjectSettings_DTO;
  /** Альбомы проекта переложены на новые размеры продукта печати; пишет сборщик, читает плашка над списком альбомов. */
  productGeometryUpdate?: ProductGeometryUpdateMark;
}

export interface ProductGeometryUpdateMark {
  /** ISO-время последней перекладки. */
  at: string;
  printProductId: string;
  /** Сколько альбомов переложено и сколько пропущено (другой лист или отказ записи). */
  updatedAlbums: number;
  skippedAlbums: number;
  /** Из пропущенных — собранные на другом листе: перекладка их не догонит, им нужна пересборка. */
  formatChanged?: number;
  /** Утверждённые, которых не трогали, и снятые с утверждения — переложенные или нет. */
  keptApproved?: number;
  revokedApproved?: number;
}

export interface FC_ProjectStatisticDTO {
  approvedAlbum: number;
  didNotEnter: number;
  editsAlbum: number;
  openedAlbum: number;
}

// #region Settings
export interface FC_ProjectSettingsDTO {
  data: ProjectData;
  /** Режим входа ученика; отсутствует у легаси-проектов (вход без защиты). */
  studentLoginMode?: 'pin' | 'links';
  /** Last person role change on «Лица» (ISO); gateway writes, albums page compares with vignette build time. */
  rolesChangedAt?: string;
  /** Etalon change stamps (ISO); server-only writer, read by `masterDelta`. */
  masterChanges?: MasterChanges;
  /** Stamps of each non-main item etalon (`masterAlbumIds` value) by its album id; the main etalon keeps `masterChanges`. */
  masterChangesByMaster?: Record<string, MasterChanges>;
}

/** Whole-album rows of the transfer window. `format` is never stamped — derived from the print product. */
export const SYNC_CATEGORY_KEYS = [
  'coverTemplate',
  'vignetteType',
  'design',
  'spreadCount',
  'watermark',
  'gap',
  'locks',
  'format',
] as const;
export type SyncCategoryKey = (typeof SYNC_CATEGORY_KEYS)[number];

/** Parts of a spread stamped apart from its content: its own gap and lock entries (the window chips of the same name). */
export const SPREAD_PART_KEYS = ['gap', 'lock'] as const;
export type SpreadPartKey = (typeof SPREAD_PART_KEYS)[number];

export interface MasterChanges {
  masterAlbumId: string;
  /** Whole etalon changed (design apply, new etalon): every item behind. */
  all?: string;
  cover?: string;
  /** spread uuid → ISO; the spread content (layout, text, photos). */
  spreads: Record<string, string>;
  /**
   * Stamps the LAST stamped save overwrote: spread uuid → the stamp it had before ('' = none); spreads that save left
   * unstamped are not listed. Lets a pass that runs after the save (quote frames) tell an album current on the spread
   * before it from one already behind. Absent on stamps written before it existed: readers keep their old behavior.
   */
  spreadsPrev?: Record<string, string>;
  /** Per spread: its content print before the same save as `spreadsPrev` ('' = none) — a there-and-back is no change. */
  printsPrev?: Record<string, string>;
  /** part → spread uuid → ISO; absent on stamps written before parts existed. */
  spreadParts?: Partial<Record<SpreadPartKey, Record<string, string>>>;
  categories: Partial<Record<SyncCategoryKey, string>>;
  /**
   * Content prints of the etalon as of its last stamped save, keyed as the stamps. A stamp written without
   * a print drops the print of its item, `all` drops every print: such an item is judged by time alone.
   */
  prints?: MasterPrints;
  /** The etalon just before its first printed save: what an album that took an item earlier holds of it. */
  base?: MasterPrintsBase;
}

/** Short hashes of the normalized etalon items; equal print = same content, whatever the stamp times. */
export interface MasterPrints {
  cover?: string;
  spreads?: Record<string, string>;
  spreadParts?: Partial<Record<SpreadPartKey, Record<string, string>>>;
  categories?: Partial<Record<SyncCategoryKey, string>>;
}

export interface MasterPrintsBase {
  /** Moment of the first printed save (ISO). */
  at: string;
  /** Stamps the project carried before that save. */
  stamps: Partial<Pick<MasterChanges, 'all' | 'cover' | 'spreads' | 'spreadParts' | 'categories'>>;
  /** Prints of the etalon before that save. */
  prints: MasterPrints;
}

/**
 * Параметры стилизации текстового поля.
 * Если не указаны — используются значения из шаблона.
 */
export interface TextStyleParams {
  fontName?: string;
  fontWeight?: string;
  fontSize?: number;
  lineHeightPx?: number;
  align?: string;
  italic?: boolean;
  color?: string;
  letterSpacing?: number;
  underline?: boolean;
  strikethrough?: boolean;
  fontCaps?: number;
  fontBaseline?: number;
  horizontalScale?: number;
  verticalScale?: number;
}

/**
 * Настройки текстового поля: текст + опциональные параметры стилизации.
 * params = undefined означает "использовать параметры из шаблона".
 */
export interface TextFieldSettings {
  text: string;
  /** Параметры стилизации. Если не заданы — используются из шаблона */
  params?: TextStyleParams;
}

export interface FC_BaseProjectSettings_DTO {
  /** Unified text settings — only project- and vignette-level keys. Person-text-types
   * (studentFullName, teacherSubject, …) resolve from Person via `applyPersonsToVignetteModel`
   * and must never appear here. */
  textSettings: Partial<Record<ProjectTextSettingKey, TextFieldSettings>>;
  cover: {
    locked: boolean;
    /** Marks `locked` as deliberately written by the editor; absent means the album
     * predates the unlocked-by-default cover and its `locked` is inherited residue. */
    lockedExplicit?: boolean;
    /**
     * Режим блокировки обложки. Ключ пишется ТОЛЬКО для 'template' — 'none' и 'full' целиком выражены
     * явной парой `locked`/`lockedExplicit`, а «Закреплённая раскладка» ей невыразима. Дефолт обложки —
     * 'none' (пары нет ⇒ обложка открыта), поэтому ключ здесь появляется у ДРУГОГО режима, чем у
     * разворота. Derive — только `resolveCoverLockMode`.
     */
    lockMode?: SpreadLockMode;
    /** Ученику разрешено править текст со своим именем на обложке (имеет смысл при lockMode !== 'full'). */
    allowOwnNameEdit?: boolean;
    /** Ось «менять тексты» обложки; ключ пишется только снятой — отсутствие значит «открыта». */
    textsOpen?: boolean;
    /**
     * Обложка потеряла фото из-за серверной чистки (перенос лиц) — «требует внимания». Ключ и его
     * семантика те же, что у разворота (`SpreadSettings.needsAttention`), derive/запись — только
     * канон `spread-attention.ts`.
     */
    needsAttention?: boolean;
    deletionLocked?: boolean;
    // FKS-9185: coverTemplateId + coverTemplate удалены — model (book.cover.collages[0]
    // .originalCustomTemplate) — единственный source of truth для cover template.
  };
  photoBook: {
    defaultSpreadCount: number;
    allowAddSpreads: boolean;
    /** Что ученику разрешено в своём альбоме; отсутствие = дефолты `StudentRightsSettings`. */
    studentRights?: StudentRightsSettings;
    minSpreadCount: number;
    maxSpreadCount: number;
    /**
     * Физический потолок продукта печати (print_products.maxSpreads) — сколько разворотов типография
     * вообще способна сшить. Отдельно от `maxSpreadCount`: тот при allowAddSpreads = клиентский потолок
     * ученика и может быть НИЖЕ физического, ограничивать фотографа им нельзя (FKS-10661).
     * Отсутствует у проектов/альбомов, сохранённых до FKS-10784 — тогда фотограф не гейтится.
     */
    productMaxSpreadCount?: number;
    spreadWithCommonPhoto: boolean;
    /** Ручки (`VignetteKnobs`) опциональны: отсутствие = `VIGNETTE_KNOB_DEFAULTS`. */
    vignette: Partial<Omit<VignetteKnobs, 'nameFormat'>> & {
      enabled: boolean;
      type: VignetteType | null;
      /**
       * Общий кроп портретов виньетки, применённый «ко всем»: один на класс. `null` (или отсутствие) —
       * общего правила нет. Пишет только фотограф.
       */
      portraitCropRule?: FC_VignettePortraitCropRule_DTO | null;
      /** Флаг использования автоматической виньетки (fallback при несовпадении количества персон) */
      usedAutomaticTemplate?: boolean;
      isSawNameHint?: boolean;
      /**
       * @deprecated Use `nameFormat` instead. Резервный фолбэк для старых сохранённых проектов:
       * true → FIRST_PATRONYMIC_SURNAME, false → SURNAME_FIRST_PATRONYMIC.
       */
      reverseNameOrder?: boolean;
      /** Формат рендера PERSON_FULL_NAME. Если не задан — derive из reverseNameOrder. */
      nameFormat?: NameFormat;
      /** Глобальный шрифт для имён на виньетке. Не задан или `null` — шрифт из шаблона; `null` в PATCH снимает его. */
      nameFont?: { fontName: string; fontWeight: string } | null;
      /** Чем упорядочены карточки. Если не задан — DEFAULT_VIGNETTE_CARD_ORDER. */
      cardOrder?: VignetteCardOrder;
      /**
       * Решение фотографа по раскладке виньетки: id пресета варианта или `'custom'` (любое другое решение).
       * Отсутствие — решения ещё не было. Ставится один раз, потом только обновляется, никогда не снимается.
       */
      pickedVariant?: string;
    };
    watermark: {
      enabled: boolean;
      value: string;
    };
    downLoadPhotos: {
      enabled: boolean;
      type: DownLoadPhotosTypesEnum;
    };
    individualSpread: IndividualSpreadSettings;
    groupSpread: GroupSpreadSettings;
    /** Личные развороты; отсутствие = DEFAULT_PERSONAL_SPREAD_SETTINGS (проекты до FKS-11162). */
    personalSpread?: PersonalSpreadSettings;
    /** Цитаты учеников; отсутствие = DEFAULT_QUOTES_SETTINGS, то есть фича выключена. */
    quotes?: QuotesSettings;
    design: {
      cover: string;
      vignette: string;
      designPackageId?: string | null;
      /**
       * The user's prompt over the pack's design narrative for this album: a partial narrative, read through
       * `readNarrativeOverrides` (editor-ai-contract; this lib cannot import it). Rides the settings PATCH whole.
       */
      narrativeOverrides?: Record<string, unknown> | null;
    };
    gapPercent: number;
    spineMarginPercent?: number;
    outerMarginPercent?: number;
    /** Отступы выставлены фотографом вручную: ноль — осознанный выбор, дефолтом не коалесится. */
    paddingUserSet?: boolean;
    /** Технология печати: lay_flat (по умолчанию) или flex_bind */
    printTechnology?: PrintTechnology;
    /**
     * UUID выбранного продукта в каталоге print_products (lg_design).
     * Резолвит размеры разворота/обложки/hingeWidth для генерации.
     */
    printProductId?: string;
    /** Все изделия проекта, главное (`printProductId`) включительно, в порядке строк. Читать через `projectPrintItems`. */
    printItems?: string[];
    /** Использовать только фото с владельцем альбома (по умолчанию: true) */
    onlyOwnerPhotos: boolean;
    otherPeoplePhotosShare: number;
    /** Закон ориентации: гориз. фото только в гориз. область, верт. — в верт. (по умолчанию: выкл). */
    enforceFrameOrientation?: boolean;
    /** Запрет обрезки тел рамкой: при срезе контента кадр показывается целиком (по умолчанию: выкл). */
    preventBodyCrop?: boolean;
    /** Холистическая постобработка альбома (дедуп по разворотам, выброс безлюдных, refit дыр; по умолчанию: выкл). */
    enableAlbumRepair?: boolean;
    /** Клиентский выбор фото учеником; отсутствие = дефолты (resolveClientSelection). */
    clientSelection?: ClientSelectionSettings;
    /** Мастер-альбом проекта — образец, от модели которого генерируются альбомы учеников. */
    masterAlbumId?: string | null;
    /** Эталон каждого НЕ главного изделия по его printProductId; главное — `masterAlbumId`. */
    masterAlbumIds?: Record<string, string>;
    /** Ученик с максимумом фото — кем становиться мастером, когда мастера нет. Сброс альбома его не стирает. */
    masterCandidatePersonId?: string | null;
  };
  spreads?: SpreadSettings[];
  /** Project-only «Ретушь» settings, mode read via `resolveRetouchMode`. Never rides into an album (ALBUM_ONLY_SETTINGS_KEYS). */
  retouch?: {
    mode?: RetouchMode;
    /** Legacy switch: still accepted on write (`false` → off, `true` → ai), stored as the mirror of `mode !== 'off'`. */
    enabled?: boolean;
    /** Deprecated: ignored, retouch opens on the finished selection (`resolveRetouchTrigger`). Stored values still validate. */
    startsOn?: RetouchStartsOn;
  };
}

export const RETOUCH_MODES = ['ai', 'manual', 'off'] as const;
export type RetouchMode = (typeof RETOUCH_MODES)[number];

/** The project's retouch mode. A row without a known `mode` reads its legacy switch: `enabled: false` → off, else ai. */
export function resolveRetouchMode(retouch: { mode?: unknown; enabled?: unknown } | null | undefined): RetouchMode {
  const mode: unknown = retouch?.mode;
  if ((RETOUCH_MODES as readonly unknown[]).includes(mode)) return mode as RetouchMode;
  return retouch?.enabled === false ? 'off' : 'ai';
}

/** Deprecated `retouch.startsOn` values: still accepted on write, read by no trigger. */
export const RETOUCH_STARTS_ON = ['mode', 'selection', 'approval'] as const;
export type RetouchStartsOn = (typeof RETOUCH_STARTS_ON)[number];

export interface PerCollageGapOverride {
  gapPercent?: number;
  spineMarginPercent?: number;
  outerMarginPercent?: number;
}

/**
 * Насколько разворот закрыт для ученика: 'none' — правит свободно, 'template' — раскладка закреплена
 * (фото заменять можно), 'full' — только просмотр. Фотографа режимы не ограничивают.
 *
 * ДЕФОЛТ задаёт проект (`studentRights.lockDefault`, без ключа — 'template'): разворот, о котором
 * фотограф ничего не сказал отдельно, ученик получает таким. Ось текстов живёт рядом (`textsOpen`), в
 * режим она не входит: три режима комбинации «раскладка без фото» не хранят и хранить не должны.
 */
export const SPREAD_LOCK_MODES = ['none', 'template', 'full'] as const;
export type SpreadLockMode = (typeof SPREAD_LOCK_MODES)[number];

/**
 * Что ученику разрешено в СВОЁМ альбоме. Отсутствие блока = дефолты: замок разворота 'template'
 * (`defaultSpreadLockMode`), правка своего имени разрешена, добавок к мастер-альбому нет.
 */
export interface StudentRightsSettings {
  /** Замок, с которым ученик получает разворот; ключа нет — 'template'. */
  lockDefault?: SpreadLockMode;
  /** Ученику разрешено править текст со своим именем. */
  allowOwnNameEdit: boolean;
  /** ДОБАВКА портретов сверх мастер-альбома, а не абсолютный потолок; null = тумблер выключен. */
  morePortraits: number | null;
  /** ДОБАВКА разворотов сверх мастер-альбома, а не абсолютный потолок; null = тумблер выключен. */
  moreSpreads: number | null;
  /** Ученик удаляет и развороты мастер-альбома; ключа нет — выкл. Читать через `projectAllowsMasterSpreadDelete`. */
  allowDeleteSpreads?: boolean;
  /** Сколько разворотов без обложки остаётся минимум при `allowDeleteSpreads`; ключа нет — 1 (`studentSpreadMinimum`). */
  minSpreadsAfterDelete?: number;
  /** Комментарии в альбоме; ключа нет — выкл., новый проект пишет true. */
  allowComments?: boolean;
  /** Ветку ученика/учителя сначала видит организатор; ключа нет — выкл. */
  organizerFirst?: boolean;
  /**
   * Общий альбом: развороты эталона закреплены, ученик меняет только фото личного; ключа нет — выкл.
   * `lockDefault` не трогает.
   */
  commonAlbum?: boolean;
}

/** Пер-страничная пара прежней панели: только на чтение, новые записи её не несут. */
export interface LegacyPagePhotoLimit {
  min: number;
  max: number;
}

/**
 * Сколько фото ученик размещает на РАЗВОРОТЕ — один потолок на обе страницы. Минимума нет: лимит
 * говорит «не больше», а не «не меньше». Ключи сторон остаются в типе ради записей прежней
 * пер-страничной панели, которые читаются через `spreadPhotoMax` и переписываются в `max`.
 */
export interface SpreadPhotoLimits {
  max?: number;
  left?: LegacyPagePhotoLimit;
  right?: LegacyPagePhotoLimit;
}

export interface SpreadSettings {
  id: number;
  slotType?: SpreadSlotTypeEnum;
  locked: boolean;
  /** Зеркало uuid разворота модели (fullSync) — стабильная идентичность, переживающая перенумерацию id. */
  uuid?: string;
  /** Провенанс клона: uuid разворота образца, из которого этот разворот скопирован (fullSync). */
  sourceSpreadUuid?: string;
  /** Блокировка удаления для клиента; отсутствие значения наследует legacy `locked` */
  deletionLocked?: boolean;
  /**
   * Режим блокировки для ученика — оси «менять фото» и «менять раскладку» вместе. Ключ пишется для всех
   * трёх режимов: дефолт задаёт сам фотограф (`studentRights.lockDefault`), и без хранимого значения
   * «ничего не сказано» не отличить от «сказано ровно то, что стоит дефолтом» — первое обязано поехать
   * за дефолтом при его смене, второе остаётся на развороте. Отсутствие ключа читается через legacy
   * `locked`: true → 'full', иначе дефолт проекта. Сам `locked` остаётся зеркалом `lockMode === 'full'` —
   * легаси-читатели и авто-замок JPEG/виньеток видят прежнюю семантику полного read-only.
   * Derive — только `resolveSpreadLockModeWithDefault`.
   */
  lockMode?: SpreadLockMode;
  /** Разворот эталона закреплён фотографом целиком. Нет ключа — легаси: закреплён, если сказан 'full' (`isPinnedSpread`). */
  pinned?: boolean;
  /** Потолок фото на весь разворот; отсутствие ключа = без лимита. */
  photoLimits?: SpreadPhotoLimits;
  /** Ученику разрешено править текст со своим именем (имеет смысл при lockMode !== 'full'). */
  allowOwnNameEdit?: boolean;
  /**
   * Ось «менять тексты», отдельная от режима: отсутствие ключа = ОТКРЫТА (`spreadTextsOpen`), поэтому
   * все прежние альбомы остаются с правкой текстов. Режим 'full' закрывает её поверх записи.
   */
  textsOpen?: boolean;
  /**
   * Виньетка: ученик меняет фото ВНЕ карточек класса (`vignetteOutsidePhotosOpen`). Ось отдельная от режима,
   * который держит свой портрет и перестановку; отсутствие ключа = дефолт: открыта, если дефолт проекта не 'full'.
   */
  outsidePhotosOpen?: boolean;
  /**
   * Ученику разрешено удалить этот разворот фотографа из своего альбома. Ключ пишется только включённым:
   * отсутствие = нельзя, поэтому прежние альбомы остаются как были. Слово отдельное от режима и пина —
   * открывает удаление и под полным замком. Виньетке и залитой картинке не выдаётся.
   */
  allowDelete?: boolean;
  /**
   * Новые развороты ученика (добавление, автосборка) встают после этого разворота эталона. Один на альбом:
   * запись на одном снимает с остальных. Ключ пишется только включённым; нет ни одного — в конец альбома.
   */
  insertAfter?: boolean;
  /**
   * Разворот потерял фото из-за серверной чистки (перенос лиц унёс последнюю привязку к владельцу) —
   * рамка осталась пустой, и разворот «требует внимания». Ключ живёт РОВНО пока стоит пометка:
   * снятие — удаление ключа, поэтому `false` в хранилище не встречается. Пометка серверная, вход
   * PATCH'а её не задаёт; derive/запись — только канон `spread-attention.ts`.
   */
  needsAttention?: boolean;
  /** Фотограф двигал/масштабировал боксы виньетки руками — пересборка разворот не перестраивает (R120). */
  manualLayout?: boolean;
  /** Серия виньетки (R208): один uuid на все её развороты; позиция 1-based и длина серии. */
  seriesId?: string;
  seriesIndex?: number;
  seriesTotal?: number;
  // FKS-9185: templateType + collageTemplateTypes удалены — model (collage.originalCustomTemplate) —
  // единственный source of truth для layout. In-memory API для applyDesignToBook / portrait
  // generator = SpreadTemplatePreferences (legacy/libs/frontend/book-generator, заморожен).
  leftCollage?: PerCollageGapOverride;
  rightCollage?: PerCollageGapOverride;
}

/** Поля серии виньетки — едут вместе; whitelist-пересборка настроек без них развалила бы серию. */
export function seriesOf({
  seriesId,
  seriesIndex,
  seriesTotal,
}: SpreadSettings): Pick<SpreadSettings, 'seriesId' | 'seriesIndex' | 'seriesTotal'> {
  return { seriesId, seriesIndex, seriesTotal };
}

/**
 * Серия виньетки книги = ровно развороты `uuids` в их порядке (одна виньетка на книгу, R113): их строки
 * получают `seriesId` и позицию 1-based, у остальных поля серии снимаются. Один разворот — не серия.
 * Строки для разворотов без настроек заводит вызывающий: без числового id строку не создать.
 */
export function tagVignetteSeries(
  settings: readonly SpreadSettings[],
  uuids: readonly string[],
  seriesId: string,
): SpreadSettings[] {
  return settings.map(row => {
    const index = row.uuid ? uuids.indexOf(row.uuid) : -1;
    if (index >= 0 && uuids.length > 1) return { ...row, seriesId, seriesIndex: index + 1, seriesTotal: uuids.length };
    if (!row.seriesId && row.seriesIndex === undefined && row.seriesTotal === undefined) return row;
    const { seriesId: _id, seriesIndex: _index, seriesTotal: _total, ...rest } = row;
    return rest;
  });
}

export type SpreadLockKind = 'edit' | 'deletion';

/** Album/preset-facing settings shape — shared cover/vignette text lives only on the project. */
export type FC_AlbumSettings_DTO = Omit<FC_BaseProjectSettings_DTO, 'textSettings'> & {
  /**
   * Ручной текст рамки цитаты — свободный текст ЭТОГО альбома при выключенных цитатах проекта.
   * Живёт в настройках альбома, а НЕ в модели: модель целиком переписывает пересборка, а подстановка
   * персон на чтении затирает бокс. Ключ проекту не принадлежит и в его настройки не попадает.
   *
   * Включение цитат его не стирает, а перестаёт показывать (подстановка снова кладёт цитату персоны),
   * поэтому повторное выключение возвращает тот же текст.
   */
  manualQuoteText?: string;
};

/**
 * Ключи настроек, которые принадлежат ОДНОМУ альбому (или только проекту, как `retouch`) и никуда не едут: ни в контракт сборки
 * (шлюз проверяет его закрытым DTO и падает на незнакомом поле), ни в снимок образца, ни в синхру.
 * Новый ключ такого рода добавляется СЮДА — вычистка на всех дорогах одна.
 */
export const ALBUM_ONLY_SETTINGS_KEYS = ['manualQuoteText', 'retouch'] as const;

/** Настройки альбома без его личных ключей — то, что можно отдать сборке или скопировать другому альбому. */
export type FC_SharedAlbumSettings_DTO = Omit<FC_AlbumSettings_DTO, (typeof ALBUM_ONLY_SETTINGS_KEYS)[number]>;

export function withoutAlbumOnlySettings(settings: FC_AlbumSettings_DTO): FC_SharedAlbumSettings_DTO {
  const shared: FC_AlbumSettings_DTO = { ...settings };
  for (const key of ALBUM_ONLY_SETTINGS_KEYS) delete shared[key];
  return shared;
}

/**
 * Product of one spread (folder). Only the product ceiling decides: a client ceiling `maxSpreadCount` of 1
 * on a legacy album is no folder.
 */
export function isSingleSpreadProduct(photoBook: { productMaxSpreadCount?: number | null } | null | undefined): boolean {
  return photoBook?.productMaxSpreadCount === 1;
}

/**
 * Ключи `photoBook`, которые задаёт ТОЛЬКО проект (политика, права, печать): при перегенерации
 * альбома от мастера они берутся из проекта, всё остальное в `photoBook` (отступы, дизайн, виньетка…)
 * — из мастера. Новый проектный ключ добавляется СЮДА.
 */
export const PROJECT_POLICY_SETTINGS_KEYS = [
  'clientSelection',
  'studentRights',
  'allowAddSpreads',
  'minSpreadCount',
  'maxSpreadCount',
  'productMaxSpreadCount',
  'defaultSpreadCount',
  'printProductId',
  'printItems',
  'printTechnology',
  'watermark',
  'downLoadPhotos',
  'onlyOwnerPhotos',
  'otherPeoplePhotosShare',
  'masterAlbumId',
  'masterAlbumIds',
  'masterCandidatePersonId',
  'enableAlbumRepair',
] as const satisfies readonly (keyof FC_BaseProjectSettings_DTO['photoBook'])[];

/** Изделия проекта: главное первым, затем остальные без повторов. Списка нет — одно главное; нет ничего — `[]`. */
export function projectPrintItems(
  settings: { photoBook?: { printProductId?: string; printItems?: readonly string[] } } | null | undefined,
): string[] {
  const main: string | undefined = settings?.photoBook?.printProductId;
  return [...new Set([...(main ? [main] : []), ...(settings?.photoBook?.printItems ?? [])])];
}

/** Эталоны проекта: главного изделия (`masterAlbumId`) и каждого другого (`masterAlbumIds`). */
export function projectMasterIds(
  photoBook: { masterAlbumId?: string | null; masterAlbumIds?: Readonly<Record<string, string>> } | null | undefined,
): ReadonlySet<string> {
  const ids = [photoBook?.masterAlbumId, ...Object.values(photoBook?.masterAlbumIds ?? {})];
  return new Set(ids.filter((id): id is string => !!id));
}

/** Эталон изделия `productId`: главное (или изделие не задано) — `masterAlbumId`, другое — `masterAlbumIds[productId]`. */
export function masterIdOf(
  settings:
    | {
        photoBook?: {
          printProductId?: string;
          masterAlbumId?: string | null;
          masterAlbumIds?: Readonly<Record<string, string>>;
        };
      }
    | null
    | undefined,
  productId?: string | null,
): string | null {
  const pb = settings?.photoBook;
  if (!productId || productId === pb?.printProductId) return pb?.masterAlbumId ?? null;
  return pb?.masterAlbumIds?.[productId] ?? null;
}

/**
 * Изделие строки альбома с продуктом `productId`: этот продукт, когда он — изделие проекта с двумя изделиями и больше;
 * иначе главное (`null`): изделие одно, продукта нет или он уже не изделие проекта (формат сменили после сборки).
 */
export function rowItemOf(
  settings: { photoBook?: { printProductId?: string; printItems?: readonly string[] } } | null | undefined,
  productId: string | null | undefined,
): string | null {
  const items: string[] = projectPrintItems(settings);
  return productId && items.length >= 2 && items.includes(productId) ? productId : null;
}

/**
 * Настройки проекта с `photoBook` мастера поверх, кроме проектных ключей (`PROJECT_POLICY_SETTINGS_KEYS`).
 * Ручки виньетки (`VignetteKnobs`) и `portraitCropRule` (едет с ручкой `crop`) — проектные: заданная у
 * проекта бьёт копию мастера, копия заполняет лишь отсутствующие (она — зеркало проекта и может отстать).
 * Без мастера (или без его `photoBook`) — `resolved` как есть. `cover`/`spreads`/`textSettings` не трогает.
 */
export function masterOwnedSettings(
  resolved: FC_BaseProjectSettings_DTO,
  master?: FC_AlbumSettings_DTO | null,
): FC_BaseProjectSettings_DTO {
  if (!master?.photoBook) return resolved;
  const owned: Partial<FC_BaseProjectSettings_DTO['photoBook']> = { ...master.photoBook };
  for (const key of PROJECT_POLICY_SETTINGS_KEYS) delete owned[key];
  if (owned.vignette) owned.vignette = withVignetteKnobs(owned.vignette, resolved.photoBook?.vignette);

  return { ...resolved, photoBook: { ...resolved.photoBook, ...owned } };
}

/**
 * Копия виньетки (снимок альбома, мастера) с ручками источника (проекта, сборки) поверх: заданная в источнике
 * ручка бьёт копию, копия заполняет лишь отсутствующие. `portraitCropRule` едет с ручкой `crop`.
 * Остальные поля копии (тип, шрифт, порядок карточек) не трогает.
 */
export function withVignetteKnobs(
  copy: FC_BaseProjectSettings_DTO['photoBook']['vignette'],
  source: FC_BaseProjectSettings_DTO['photoBook']['vignette'] | null | undefined,
): FC_BaseProjectSettings_DTO['photoBook']['vignette'] {
  const merged = { ...copy, ...pickVignetteKnobs(source) };
  if (source?.portraitCropRule !== undefined) merged.portraitCropRule = source.portraitCropRule;

  return merged;
}
// #endregion Settings
